import React, { Fragment, useMemo, useState } from 'react';
import { useAITTheme, type AITTheme } from '@this/shared/ui/theme';
import {
  PROPOSAL_BUNDLE_CATEGORY,
  PROPOSAL_BUNDLE_STATE,
  findSelectedTransportItem,
  findTransportItemById,
  isHotelProposalBundle,
  isTransportProposalBundle,
  type HotelProposalBundle,
  type ProposalBundle,
  type TransportProposalBundle,
  type TransportProposalItem
} from '../../types/proposalBundles';
import type {
  OnProposalBundleClose,
  OnProposalBundleFeedbackUpdate,
  OnProposalItemSelect
} from '../../types/proposalHandlers';
import type { ChatMessage } from '../../types/messages';
import AiAvatar from '../icons/AiAvatar';
import { AnimatedMessageRow } from '../AnimatedMessageRow';
import TransportCard from './transport/TransportCard';
import HotelCard from './hotel/HotelCard';
import ForeignFlightLegCard from './foreign_flight/ForeignFlightLegCard';
import { ForeignFlightProposalInstruction } from './foreign_flight/ForeignFlightProposalInstruction';
import { formatTimestamp } from '../../utils/formatTimestamp';
interface Props {
  message: ChatMessage;
  bundles: ProposalBundle[];
  tripId: number;
  includeTax: boolean;
  disabled?: boolean;
  onSelectItem?: OnProposalItemSelect;
  onCloseItem?: OnProposalBundleClose;
  onBundleFeedbackUpdate?: OnProposalBundleFeedbackUpdate;
  isSpLayout?: boolean;
}

const FOREIGN_FLIGHT_PROPOSAL_SECTION_TYPE = {
  FIRST_LEG: 'firstLeg',
  HOMEWARD: 'homeward',
  HOTELS: 'hotels'
} as const;

type ForeignFlightProposalSectionId =
  typeof FOREIGN_FLIGHT_PROPOSAL_SECTION_TYPE[keyof typeof FOREIGN_FLIGHT_PROPOSAL_SECTION_TYPE];

type ForeignFlightProposalDisplaySection = {
  id: ForeignFlightProposalSectionId;
  message: string;
  showInstruction: boolean;
};

type ForeignFlightProposalViewModel = {
  outward: TransportProposalBundle | undefined;
  homeward: TransportProposalBundle | undefined;
  outwardDisplayItems: TransportProposalItem[];
  homewardRows: TransportProposalItem[];
  outwardHasSelection: boolean;
  homewardHasSelection: boolean;
  pairId: string | null;
  outwardSelectHandler: OnProposalItemSelect;
  homewardEmptyMessage: string | null;
  outwardEmptyMessage: string | null;
  restTransport: TransportProposalBundle[];
  restHotels: HotelProposalBundle[];
  transportLegCount: number;
  sections: ForeignFlightProposalDisplaySection[];
};

type BuildViewModelArgs = {
  bundles: ProposalBundle[];
  introMessage: string;
  pairId: string | null;
  onSelect?: OnProposalItemSelect;
  onOutwardPairSelect?: (pairId: string) => void;
};

type ForeignFlightChatLayout = {
  transportLegCount: number;
  showHotelRow: boolean;
  lastRowIsHomeward: boolean;
  showTimestampOnFirstRow: boolean;
};

const pickStagedVisibleBundles = (bundles: ProposalBundle[]): ProposalBundle[] => {
  const outward = bundles.find(
    bundle => bundle.category === PROPOSAL_BUNDLE_CATEGORY.OUTWARD && bundle.hasForeignFlightRoundTrip
  );
  const homeward = bundles.find(
    bundle => bundle.category === PROPOSAL_BUNDLE_CATEGORY.HOMEWARD && bundle.hasForeignFlightRoundTrip
  );
  if (!outward || !homeward) {
    return bundles;
  }

  const rest = bundles.filter(bundle => bundle.id !== outward.id && bundle.id !== homeward.id);
  if (outward.state === PROPOSAL_BUNDLE_STATE.SUGGESTING) {
    return [outward];
  }

  if (homeward.state === PROPOSAL_BUNDLE_STATE.SUGGESTING) {
    const withoutHotels = rest.filter(bundle => bundle.category !== PROPOSAL_BUNDLE_CATEGORY.HOTEL);
    return [outward, homeward, ...withoutHotels];
  }

  return [outward, homeward, ...rest];
};

const isForeignFlightRoundTripOutwardOrHomewardLeg = (bundle: ProposalBundle): boolean =>
  isTransportProposalBundle(bundle) &&
  Boolean(bundle.hasForeignFlightRoundTrip) &&
  (bundle.category === PROPOSAL_BUNDLE_CATEGORY.OUTWARD || bundle.category === PROPOSAL_BUNDLE_CATEGORY.HOMEWARD);

const filterForeignFlightRoundTripRestBundles = (visible: ProposalBundle[]): ProposalBundle[] =>
  visible.filter(bundle => !isForeignFlightRoundTripOutwardOrHomewardLeg(bundle));

const uniqueForeignFlightItemsByDisplayKey = (items: TransportProposalItem[]): TransportProposalItem[] => {
  const seen = new Set<string>();
  const result: TransportProposalItem[] = [];
  for (const item of items) {
    if (seen.has(item.transport.foreignRoundTripDisplayKey)) {
      continue;
    }
    seen.add(item.transport.foreignRoundTripDisplayKey);
    result.push(item);
  }
  return result;
};

const computeForeignFlightChatLayout = (
  outward: TransportProposalBundle | undefined,
  homeward: TransportProposalBundle | undefined,
  showHomewardCard: boolean,
  restHotels: HotelProposalBundle[]
): ForeignFlightChatLayout => {
  const transportLegCount = (outward ? 1 : 0) + (showHomewardCard && homeward ? 1 : 0);
  const showHotelRow =
    restHotels.length > 0 && homeward != null && homeward.state !== PROPOSAL_BUNDLE_STATE.SUGGESTING;
  const lastRowIsHomeward = showHomewardCard && homeward != null && outward != null && !showHotelRow;
  const showTimestampOnFirstRow = !showHomewardCard || (homeward != null && outward == null);
  return {
    transportLegCount,
    showHotelRow,
    lastRowIsHomeward,
    showTimestampOnFirstRow
  };
};

const buildForeignFlightProposalViewModel = ({
  bundles,
  introMessage,
  pairId,
  onOutwardPairSelect,
  onSelect
}: BuildViewModelArgs): ForeignFlightProposalViewModel => {
  const visible = pickStagedVisibleBundles(bundles);
  const outward = bundles.find(
    (bundle): bundle is TransportProposalBundle =>
      isTransportProposalBundle(bundle) &&
      bundle.category === PROPOSAL_BUNDLE_CATEGORY.OUTWARD &&
      Boolean(bundle.hasForeignFlightRoundTrip)
  );
  const homeward = bundles.find(
    (bundle): bundle is TransportProposalBundle =>
      isTransportProposalBundle(bundle) &&
      bundle.category === PROPOSAL_BUNDLE_CATEGORY.HOMEWARD &&
      Boolean(bundle.hasForeignFlightRoundTrip)
  );
  const homewardPairIds = new Set(
    (homeward?.items ?? [])
      .map(item => item.transport.roundTripFlightId)
      .filter(
        (roundTripFlightId): roundTripFlightId is string => roundTripFlightId != null && roundTripFlightId !== ''
      )
      .map(String)
  );
  const outwardDisplayItems =
    outward == null
      ? []
      : homeward == null
      ? uniqueForeignFlightItemsByDisplayKey(outward.items)
      : uniqueForeignFlightItemsByDisplayKey(
          outward.items.filter(item => {
            const roundTripFlightId = item.transport.roundTripFlightId;
            return (
              roundTripFlightId != null &&
              roundTripFlightId !== '' &&
              homewardPairIds.has(String(roundTripFlightId))
            );
          })
        );
  const resolvedOutwardDisplayItems =
    outward != null && homeward != null && outwardDisplayItems.length === 0
      ? uniqueForeignFlightItemsByDisplayKey(outward.items)
      : outwardDisplayItems;
  const outwardHasSelection = outward?.items.some(item => item.selected) ?? false;
  const homewardHasSelection = homeward?.items.some(item => item.selected) ?? false;
  const showHomewardCard = Boolean(homeward && (outwardHasSelection || outward == null));
  const rest = filterForeignFlightRoundTripRestBundles(visible);
  const restTransport = rest.filter(
    (bundle): bundle is TransportProposalBundle =>
      isTransportProposalBundle(bundle) &&
      (bundle.category === PROPOSAL_BUNDLE_CATEGORY.OUTWARD ||
        bundle.category === PROPOSAL_BUNDLE_CATEGORY.HOMEWARD ||
        bundle.category === PROPOSAL_BUNDLE_CATEGORY.TRANSPORT)
  );
  const restHotels = rest.filter(isHotelProposalBundle);
  const chatLayout: ForeignFlightChatLayout = computeForeignFlightChatLayout(
    outward,
    homeward,
    showHomewardCard,
    restHotels
  );
  const outwardSelectHandler: OnProposalItemSelect = (bundleId, itemId, applyUpdate) => {
    const selectedItem = outward != null ? findTransportItemById(outward.items, itemId) : null;
    if (selectedItem?.transport.roundTripFlightId) {
      onOutwardPairSelect?.(selectedItem.transport.roundTripFlightId);
    }
    onSelect?.(bundleId, itemId, applyUpdate);
  };
  const homewardRows =
    homeward == null || !showHomewardCard
      ? []
      : outward == null
      ? uniqueForeignFlightItemsByDisplayKey(homeward.items)
      : pairId == null
      ? []
      : uniqueForeignFlightItemsByDisplayKey(
          homeward.items.filter(item => String(item.transport.roundTripFlightId ?? '') === String(pairId))
        );
  const resolvedHomewardRows =
    homeward != null && outward != null && pairId != null && homewardRows.length === 0
      ? uniqueForeignFlightItemsByDisplayKey(homeward.items)
      : homewardRows;
  const homewardEmptyMessage =
    !showHomewardCard || homeward == null
      ? null
      : outward != null && !pairId
      ? '先に往路を選んでください。'
      : resolvedHomewardRows.length === 0
      ? 'この往路に対応する復路候補がありません。'
      : null;
  const outwardEmptyMessage =
    outward != null && resolvedOutwardDisplayItems.length === 0 ? '復路と対応する往路候補がありません。' : null;
  const sections: ForeignFlightProposalDisplaySection[] = [
    {
      id: FOREIGN_FLIGHT_PROPOSAL_SECTION_TYPE.FIRST_LEG,
      message: introMessage,
      showInstruction: chatLayout.showTimestampOnFirstRow
    }
  ];

  if (showHomewardCard && homeward != null && outward != null) {
    sections.push({
      id: FOREIGN_FLIGHT_PROPOSAL_SECTION_TYPE.HOMEWARD,
      message: '復路の航空券をお選びください。往路の選択は確定しています。',
      showInstruction: chatLayout.lastRowIsHomeward
    });
  }

  if (chatLayout.showHotelRow) {
    sections.push({
      id: FOREIGN_FLIGHT_PROPOSAL_SECTION_TYPE.HOTELS,
      message: 'ホテルなどの候補をお選びください。往復航空券の料金は確定しています。',
      showInstruction: chatLayout.showHotelRow
    });
  }

  return {
    outward,
    homeward,
    outwardDisplayItems: resolvedOutwardDisplayItems,
    homewardRows: resolvedHomewardRows,
    outwardHasSelection,
    homewardHasSelection,
    pairId,
    outwardSelectHandler,
    homewardEmptyMessage,
    outwardEmptyMessage,
    restTransport,
    restHotels,
    transportLegCount: chatLayout.transportLegCount,
    sections
  };
};

const getForeignFlightSelectedPairId = (bundles: ProposalBundle[]): string | null => {
  const outward = bundles.find(
    bundle => bundle.category === PROPOSAL_BUNDLE_CATEGORY.OUTWARD && bundle.hasForeignFlightRoundTrip
  );
  const homeward = bundles.find(
    bundle => bundle.category === PROPOSAL_BUNDLE_CATEGORY.HOMEWARD && bundle.hasForeignFlightRoundTrip
  );
  const fromOutward =
    outward != null && isTransportProposalBundle(outward)
      ? findSelectedTransportItem(outward.items)?.transport.roundTripFlightId
      : undefined;
  if (fromOutward != null && fromOutward !== '') {
    return fromOutward;
  }
  if (!outward && homeward && isTransportProposalBundle(homeward)) {
    return findSelectedTransportItem(homeward.items)?.transport.roundTripFlightId ?? null;
  }
  return null;
};

export const ForeignFlightProposalSection = ({
  message,
  bundles,
  tripId,
  includeTax,
  disabled = false,
  onSelectItem,
  onCloseItem,
  onBundleFeedbackUpdate,
  isSpLayout = false
}: Props) => {
  const { theme } = useAITTheme();
  const styles = useMemo(() => getStyles(theme), [theme]);
  const [outwardPairId, setOutwardPairId] = useState<string | null>(null);

  const selectedPairId = getForeignFlightSelectedPairId(bundles);
  const viewModel = buildForeignFlightProposalViewModel({
    bundles,
    introMessage: message.text ?? '',
    pairId: outwardPairId ?? selectedPairId,
    onSelect: onSelectItem,
    onOutwardPairSelect: setOutwardPairId
  });

  return (
    <Fragment>
      {viewModel.sections.map(section => (
        <AnimatedMessageRow key={`${message.id}-${section.id}`} isNew={message.isNew} isSpLayout={isSpLayout}>
          <div style={styles.avatar}>
            <AiAvatar />
          </div>
          <div style={styles.messageContentAiProposal}>
            <div style={styles.aiBubble}>{section.message}</div>
            {section.id === FOREIGN_FLIGHT_PROPOSAL_SECTION_TYPE.FIRST_LEG && (
              <div style={styles.cardStack}>
                {viewModel.outward ? (
                  <ForeignFlightLegCard
                    stepLabel="1"
                    title="往路"
                    bundle={viewModel.outward}
                    items={viewModel.outwardDisplayItems}
                    includeTax={includeTax}
                    onSelect={viewModel.outwardSelectHandler}
                    onClose={onCloseItem}
                    onFeedbackUpdate={onBundleFeedbackUpdate}
                    tripId={tripId}
                    disabled={disabled}
                    lockSelection={viewModel.outwardHasSelection}
                    emptyMessage={viewModel.outwardEmptyMessage}
                  />
                ) : viewModel.homeward ? (
                  <ForeignFlightLegCard
                    stepLabel="1"
                    title="復路"
                    bundle={viewModel.homeward}
                    items={viewModel.homewardRows}
                    includeTax={includeTax}
                    onSelect={onSelectItem}
                    onClose={onCloseItem}
                    onFeedbackUpdate={onBundleFeedbackUpdate}
                    tripId={tripId}
                    disabled={disabled}
                    lockSelection={viewModel.homewardHasSelection}
                    selectionDisabled={false}
                    emptyMessage={viewModel.homewardEmptyMessage}
                  />
                ) : null}
              </div>
            )}
            {section.id === FOREIGN_FLIGHT_PROPOSAL_SECTION_TYPE.HOMEWARD && (
              <div style={styles.cardStack}>
                {viewModel.homeward && (
                  <ForeignFlightLegCard
                    stepLabel="2"
                    title="復路"
                    bundle={viewModel.homeward}
                    items={viewModel.homewardRows}
                    includeTax={includeTax}
                    onSelect={onSelectItem}
                    onClose={onCloseItem}
                    onFeedbackUpdate={onBundleFeedbackUpdate}
                    tripId={tripId}
                    disabled={disabled}
                    lockSelection={viewModel.homewardHasSelection}
                    selectionDisabled={!viewModel.pairId}
                    emptyMessage={viewModel.homewardEmptyMessage}
                  />
                )}
                {viewModel.restTransport.map((bundle, index) => {
                  const productNumber = viewModel.transportLegCount + index + 1;
                  return (
                    <TransportCard
                      key={bundle.id}
                      bundle={bundle}
                      productNumber={productNumber}
                      includeTax={includeTax}
                      onSelect={onSelectItem}
                      onClose={onCloseItem}
                      onFeedbackUpdate={onBundleFeedbackUpdate}
                      tripId={tripId}
                      disabled={disabled}
                    />
                  );
                })}
              </div>
            )}
            {section.id === FOREIGN_FLIGHT_PROPOSAL_SECTION_TYPE.HOTELS && (
              <div style={styles.cardStack}>
                {viewModel.restHotels.map((bundle, index) => {
                  const productNumber = viewModel.transportLegCount + viewModel.restTransport.length + index + 1;
                  return (
                    <Fragment key={bundle.id}>
                      {index === 0 && <div style={styles.hotelSectionTitle}>ホテル</div>}
                      <HotelCard
                        bundle={bundle}
                        productNumber={productNumber}
                        includeTax={includeTax}
                        onSelect={onSelectItem}
                        onClose={onCloseItem}
                        onFeedbackUpdate={onBundleFeedbackUpdate}
                        disabled={disabled}
                        tripId={tripId}
                      />
                    </Fragment>
                  );
                })}
              </div>
            )}
            {section.showInstruction && (
              <Fragment>
                <ForeignFlightProposalInstruction />
                <div style={styles.timestamp}>{formatTimestamp(message.createdAt)}</div>
              </Fragment>
            )}
          </div>
        </AnimatedMessageRow>
      ))}
    </Fragment>
  );
};

const getStyles = (_theme: AITTheme) => ({
  avatar: {
    flex: '0 0 48px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  },
  messageContentAiProposal: {
    flex: 1,
    minWidth: 0,
    maxWidth: '100%'
  },
  aiBubble: {
    padding: '12px 16px',
    borderRadius: '8px',
    backgroundColor: '#f5f5f5',
    display: 'inline-block' as const
  },
  timestamp: {
    fontSize: '11px',
    color: '#7d7d7d',
    marginTop: '4px'
  },
  cardStack: {
    marginTop: '12px'
  },
  hotelSectionTitle: {
    fontSize: '14px',
    fontWeight: 700 as const,
    color: '#333333',
    marginBottom: '-8px'
  }
});
